/**
 * useChat - state + scheduling for simulated author chats.
 *
 * Owns the chat list, exposes open/send, and runs a single self-rescheduling
 * timer that delivers pending author replies when their `deliverAt` arrives.
 * On mount (and whenever pending state changes) it first flushes any replies
 * that became due while away, then schedules a timer to the next one - so the
 * 2-minute delay is honoured precisely without polling.
 */
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import {
  loadChats,
  saveChats,
  openChat as openChatPure,
  addUserMessage,
  editMessage,
  addCallEnded,
  tickChats,
  chatIdFor,
  markRead,
  totalUnread,
} from '../lib/chat.js';

export function useChat() {
  const [chats, setChats] = useState(() => {
    // Flush anything that came due while the app was closed.
    const { list } = tickChats(loadChats());
    saveChats(list);
    return list;
  });
  const timer = useRef(0);

  const persist = (list) => {
    saveChats(list);
    return list;
  };

  // Reschedule the tick timer whenever chats change. A tick first flips pending
  // replies into the "typing" phase, then delivers those whose time has come,
  // so the typing indicator only shows after the human-like notice delay.
  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = 0;
    }
    const { list, changed, nextAt } = tickChats(chats);
    if (changed) {
      setChats(persist(list));
      return undefined; // effect will re-run with the new list
    }
    if (nextAt != null) {
      const delay = Math.max(0, nextAt - Date.now());
      timer.current = window.setTimeout(() => {
        timer.current = 0;
        setChats((prev) => persist(tickChats(prev).list));
      }, delay);
    }
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = 0;
      }
    };
  }, [chats]);

  const open = useCallback((author) => {
    setChats((prev) => {
      const { list } = openChatPure(prev, author);
      return list === prev ? prev : persist(list);
    });
    // Deterministic id lets the caller navigate immediately.
    return chatIdFor(author.authorId, author.channel);
  }, []);

  const send = useCallback((chatId, text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    setChats((prev) => persist(addUserMessage(prev, chatId, { text: trimmed })));
  }, []);

  // Send a media attachment (already stored in mediaStore) with optional text.
  const sendMedia = useCallback((chatId, media, text = '') => {
    if (!media) return;
    setChats((prev) =>
      persist(addUserMessage(prev, chatId, { text: (text || '').trim(), media })),
    );
  }, []);

  const edit = useCallback((chatId, msgId, text) => {
    setChats((prev) => {
      const next = editMessage(prev, chatId, msgId, text);
      return next === prev ? prev : persist(next);
    });
  }, []);

  // Record an ended call (and maybe a follow-up message).
  const endCall = useCallback((chatId, durationSec) => {
    setChats((prev) => persist(addCallEnded(prev, chatId, durationSec)));
  }, []);

  const read = useCallback((chatId) => {
    setChats((prev) => {
      const next = markRead(prev, chatId);
      return next === prev ? prev : persist(next);
    });
  }, []);

  return { chats, open, send, sendMedia, edit, endCall, read, unread: totalUnread(chats) };
}
